/**
 * Integrations › Meta for the demo client: an in-memory connection per tenant.
 *
 * The 676 demo tenant starts connected, with two ad accounts (one in the
 * tenant's currency and mapped, one in USD and not), so the mapping screen and
 * its currency warning are visible with no backend. Northfield starts on an
 * environment with no Meta app configured, so the "Not configured" state is too.
 * Any other tenant starts not connected. "Connect" completes at once and sends
 * the browser to the page the real callback would.
 *
 * The import health is demo data with the same shape the API sends: the 676 tenant
 * has a hierarchy run and a fresh insights run, so the card's normal state is
 * visible; `requireReconnect` and `delayProvider` put it into the two states worth
 * seeing without a backend.
 */

import type { CampaignWebsite } from '@/api/campaigns'
import type { MetaConnectStart } from '@/api/integrations'
import type {
  MetaAdAccountList,
  MetaAdAccountMapping,
  MetaAvailableAdAccount,
  MetaImportRun,
  MetaIntegrationState,
  MetaLastImports,
  MetaMappedAdAccount,
  MetaMappingResult,
} from '@/api/types'
import { ApiError } from '@/lib/api'

type DemoStart = 'connected' | 'not_connected' | 'not_configured'

const DEMO_START: Record<string, DemoStart> = {
  demo_676: 'connected',
  demo_northfield: 'not_configured',
}

const DAY_MS = 24 * 60 * 60 * 1000
const HOUR_MS = 60 * 60 * 1000
/** The API's own rule (`health.PROVIDER_LAG_AFTER`), so the demo lags when it would. */
const PROVIDER_LAG_AFTER_MS = 2 * HOUR_MS

interface DemoAccount {
  id: string
  name: string
  currency: string
  timezone: string
}

const DEMO_ACCOUNTS: readonly DemoAccount[] = [
  { id: 'act_100001', name: 'Main brand', currency: 'GBP', timezone: 'Europe/London' },
  { id: 'act_100002', name: 'US retargeting', currency: 'USD', timezone: 'America/New_York' },
]

export class MockMetaStore {
  private status: MetaIntegrationState['status'] = 'not_connected'
  private readonly configured: boolean
  private lastError: string | null = null
  private connectedAt: Date | null = null
  /** When the half-hourly insights refresh last succeeded; null when it never has. */
  private lastInsightsAt: Date | null = null
  private lastHierarchyAt: Date | null = null
  /** external ad account id → website id */
  private mapping = new Map<string, string>()

  constructor(
    tenantId: string,
    private readonly currency: string,
    private readonly websites: () => CampaignWebsite[],
    private readonly now: () => Date,
  ) {
    const start = DEMO_START[tenantId] ?? 'not_connected'
    this.configured = start !== 'not_configured'
    if (start === 'connected') {
      this.status = 'connected'
      this.connectedAt = new Date(now().getTime() - 13 * DAY_MS)
      this.lastHierarchyAt = new Date(now().getTime() - 3 * HOUR_MS)
      this.lastInsightsAt = new Date(now().getTime() - 12 * 60 * 1000)
      const production = websites().find((website) => website.environment === 'production')
      if (production && DEMO_ACCOUNTS[0]) this.mapping.set(DEMO_ACCOUNTS[0].id, production.id)
    }
  }

  state(): MetaIntegrationState {
    const live = this.status !== 'not_connected'
    return {
      configured: this.configured,
      missingConfiguration: this.configured ? [] : ['COMPETITION_INSIGHTS_META_APP_ID', 'COMPETITION_INSIGHTS_META_APP_SECRET'],
      status: this.status,
      connectedUserName: live ? 'Casey Marketing' : null,
      scopes: live ? ['ads_read', 'public_profile'] : [],
      tokenExpiresAt: live && this.connectedAt ? new Date(this.connectedAt.getTime() + 60 * DAY_MS).toISOString() : null,
      connectedAt: this.connectedAt?.toISOString() ?? null,
      lastCheckedAt: live ? new Date(this.now().getTime() - 6 * 60 * 60 * 1000).toISOString() : null,
      lastError: this.lastError,
      tenantCurrency: this.currency,
      refreshWindowDays: 14,
      adAccounts: this.mapped(),
      lastImports: this.lastImports(),
      providerLag: this.lagging(),
    }
  }

  private lastImports(): MetaLastImports {
    return {
      hierarchy: run(this.lastHierarchyAt, 412),
      insights: run(this.lastInsightsAt, 86),
    }
  }

  /** The API's rule: connected, something mapped, and no fresh `recent` run. */
  private lagging(): boolean {
    if (this.status !== 'connected' || this.mapping.size === 0) return false
    if (!this.lastInsightsAt) return true
    return this.now().getTime() - this.lastInsightsAt.getTime() > PROVIDER_LAG_AFTER_MS
  }

  /** "Run import now": both imports, as the API runs them, and the state after. */
  runImportNow(): MetaIntegrationState {
    this.requireConnected()
    if (this.mapping.size === 0) {
      throw new ApiError(
        422,
        'validation_error',
        'No Meta ad account feeds a website yet. Choose a website for at least one ad account, and its import starts on its own.',
      )
    }
    this.lastHierarchyAt = this.now()
    this.lastInsightsAt = this.now()
    return this.state()
  }

  /** What the demo does when the half-hourly refresh has not run for hours. */
  delayProvider(): void {
    this.lastInsightsAt = new Date(this.now().getTime() - 5 * HOUR_MS)
  }

  connect(): MetaConnectStart {
    this.requireConfigured()
    this.status = 'connected'
    this.lastError = null
    this.connectedAt = this.now()
    // The real flow leaves for facebook.com; the demo lands where its callback would.
    return { authorizeUrl: '/integrations/meta?connected=1', completesInApp: true }
  }

  disconnect(): MetaIntegrationState {
    this.requireConfigured()
    this.status = 'not_connected'
    this.connectedAt = null
    this.lastError = null
    this.lastHierarchyAt = null
    this.lastInsightsAt = null
    return this.state()
  }

  /** What the daily check does when Meta stops accepting the connection. */
  requireReconnect(reason: string): void {
    this.status = 'reconnect_required'
    this.lastError = reason
  }

  adAccounts(): MetaAdAccountList {
    this.requireConnected()
    return {
      tenantCurrency: this.currency,
      items: DEMO_ACCOUNTS.map(
        (account): MetaAvailableAdAccount => ({
          externalAdAccountId: account.id,
          accountId: account.id.replace('act_', ''),
          name: account.name,
          currency: account.currency,
          timezoneName: account.timezone,
          accountStatus: 'active',
          websiteId: this.mapping.get(account.id) ?? null,
          currencyMatchesTenant: account.currency === this.currency,
        }),
      ),
    }
  }

  save(mappings: readonly MetaAdAccountMapping[]): MetaMappingResult {
    this.requireConnected()
    const websites = new Set(this.websites().map((website) => website.id))
    for (const mapping of mappings) {
      if (!DEMO_ACCOUNTS.some((account) => account.id === mapping.externalAdAccountId)) {
        throw new ApiError(422, 'validation_error', 'The connected Meta user has no role on that ad account.')
      }
      if (!websites.has(mapping.websiteId)) throw new ApiError(404, 'tenant_scope_error', 'resource not found in this tenant')
    }
    const next = new Map(mappings.map((mapping) => [mapping.externalAdAccountId, mapping.websiteId]))
    const unmapped = [...this.mapping.keys()].filter((id) => !next.has(id)).sort()
    this.mapping = next
    const warnings = DEMO_ACCOUNTS.filter((account) => next.has(account.id) && account.currency !== this.currency).map(
      (account) => ({
        externalAdAccountId: account.id,
        code: 'currency_mismatch' as const,
        message: `${account.name} reports spend in ${account.currency}, but this workspace reports in ${this.currency}. Spend will be converted at the daily rate when it is imported.`,
      }),
    )
    return { items: this.mapped(), unmapped, warnings }
  }

  private mapped(): MetaMappedAdAccount[] {
    const websites = this.websites()
    return DEMO_ACCOUNTS.flatMap((account) => {
      const websiteId = this.mapping.get(account.id)
      const website = websites.find((candidate) => candidate.id === websiteId)
      if (!website) return []
      return [
        {
          adAccountId: `adacc_demo_${account.id}`,
          externalAdAccountId: account.id,
          name: account.name,
          currency: account.currency,
          timezone: account.timezone,
          websiteId: website.id,
          environment: website.environment,
          status: 'active',
        },
      ]
    })
  }

  private requireConfigured(): void {
    if (!this.configured) {
      throw new ApiError(
        503,
        'provider_unavailable',
        'Meta is not configured on this environment. Missing: COMPETITION_INSIGHTS_META_APP_ID, COMPETITION_INSIGHTS_META_APP_SECRET',
      )
    }
  }

  private requireConnected(): void {
    this.requireConfigured()
    if (this.status === 'not_connected') throw new ApiError(422, 'validation_error', 'Meta is not connected. Connect Meta first.')
    if (this.status === 'reconnect_required') {
      throw new ApiError(422, 'validation_error', 'The Meta connection needs reconnecting before ad accounts can be read.')
    }
  }
}

function run(at: Date | null, rowsWritten: number): MetaImportRun | null {
  if (!at) return null
  return {
    startedAt: new Date(at.getTime() - 40_000).toISOString(),
    finishedAt: at.toISOString(),
    status: 'succeeded',
    rowsWritten,
    error: null,
  }
}

/** One store per demo client and tenant, so a session's changes survive navigation. */
const stores = new WeakMap<object, Map<string, MockMetaStore>>()

export function metaStoreFor(
  owner: object,
  tenant: { id: string; currency: string },
  websites: () => CampaignWebsite[],
  now: () => Date,
): MockMetaStore {
  let byTenant = stores.get(owner)
  if (!byTenant) {
    byTenant = new Map()
    stores.set(owner, byTenant)
  }
  let store = byTenant.get(tenant.id)
  if (!store) {
    store = new MockMetaStore(tenant.id, tenant.currency, websites, now)
    byTenant.set(tenant.id, store)
  }
  return store
}
